<template>
  <ModalShell :visible="visible" :transition-name="transitionName" :show-overlay="isOverlay" :close-on-overlay="false" :interactive="isOverlay" :z-index="zIndex" :padding="isOverlay ? '32px' : '0px'" @close="emit('close')">
    <div class="appearance_floating_shell" :class="{ overlay_mode: isOverlay }">
      <div class="appearance_floating_anchor" :class="{ centered_mode: isOverlay }" :style="anchorStyle">
        <div class="appearance_floating_panel" :class="{ has_preview: isOverlay && hasPreview }" :style="panelStyle">
          <div class="appearance_floating_header">
            <div class="header_main">
              <div class="header_title">{{ title }}</div>
            </div>

            <div class="header_actions">
              <div class="auto_saved_note">{{ autoSavedText }}</div>
              <button type="button" class="mgx_action_btn" @click="emit('reset')">
                <div class="icon-wrapper">
                  <SvgAsset :content="actionResetSvg" />
                </div>
                <span>{{ resetText }}</span>
              </button>
              <div class="mgx_close_btn" @click="emit('close')">
                <SvgAsset :content="windowCloseSvg" />
              </div>
            </div>
          </div>

          <div v-if="isOverlay && hasPreview" class="appearance_floating_preview">
            <slot name="preview" />
          </div>

          <div class="appearance_floating_body">
            <slot />
          </div>
        </div>
      </div>
    </div>
  </ModalShell>
</template>

<script setup lang="ts">
/**
 * 主题 / 键盘外观浮动面板的通用外壳。
 *
 * 双模式架构：
 *   - SelectorView (isOverlay=true)：居中遮罩，可内嵌预览插槽
 *   - 设备详情页 (isOverlay=false)：右侧侧滑面板，无预览
 *
 * 宿主通过 4 个尺寸 props 控制布局差异，避免每个 floating 重复一份
 * media query 与 width/height 计算式。
 */
import { computed, useSlots } from 'vue'
import ModalShell from '@/components/common/ModalShell.vue'
import SvgAsset from '@/components/common/SvgAsset.vue'
import actionResetSvg from '@/assets/inline-svg/action-reset.svg?raw'
import windowCloseSvg from '@/assets/inline-svg/window-close.svg?raw'

const props = withDefaults(
  defineProps<{
    visible: boolean
    title: string
    autoSavedText: string
    resetText: string
    transitionName: string
    zIndex: number
    isOverlay: boolean
    /** 浮动模式（设备详情页）面板宽度 */
    defaultWidth: string
    /** 浮动模式面板最大高度 */
    defaultMaxHeight: string
    /** Overlay 模式（selector 页）面板宽度；带预览时通常更大 */
    overlayWidth: string
    /** Overlay 模式面板最大高度 */
    overlayMaxHeight: string
    /** 浮动模式 anchor 的 top 偏移 */
    anchorTop?: string
    /** 浮动模式 anchor 的 right 偏移 */
    anchorRight?: string
  }>(),
  {
    anchorTop: '76px',
    anchorRight: 'calc(var(--sidebar-right-width-v5) - 84px)',
  },
)

const emit = defineEmits<{
  close: []
  reset: []
}>()

const slots = useSlots()
const hasPreview = computed(() => Boolean(slots.preview))

const anchorStyle = computed(() => ({
  '--appearance-anchor-top': props.anchorTop,
  '--appearance-anchor-right': props.anchorRight,
  '--appearance-anchor-z-index': String(props.zIndex),
}))

const panelStyle = computed(() => ({
  '--appearance-panel-default-width': props.defaultWidth,
  '--appearance-panel-default-max-height': props.defaultMaxHeight,
  '--appearance-panel-overlay-width': props.overlayWidth,
  '--appearance-panel-overlay-max-height': props.overlayMaxHeight,
}))
</script>

<style scoped lang="less">
/* ── Shell / Anchor 双模式架构 ── */

.appearance_floating_shell {
  width: 100%;
  min-height: 100%;
  pointer-events: none;

  &.overlay_mode {
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: auto;
  }
}

.appearance_floating_anchor {
  position: fixed;
  top: var(--appearance-anchor-top);
  right: var(--appearance-anchor-right);
  z-index: var(--appearance-anchor-z-index);
  display: flex;
  justify-content: flex-end;
  pointer-events: none;

  &.centered_mode {
    position: static;
    width: 100%;
    min-height: 0;
    justify-content: center;
    align-items: center;
    padding: 0;
  }
}

/* ── 浮动面板主体 ── */

.appearance_floating_panel {
  width: var(--appearance-panel-default-width);
  max-height: var(--appearance-panel-default-max-height);
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-l);
  background: var(--glass-panel-bg);
  backdrop-filter: var(--glass-panel-blur);
  -webkit-backdrop-filter: var(--glass-panel-blur);
  border: var(--panel-border-soft);
  overflow: hidden;
  box-shadow: var(--shadow-panel);
  pointer-events: auto;
  contain: layout paint;
  will-change: transform, opacity;

  .overlay_mode & {
    width: var(--appearance-panel-overlay-width);
    max-height: var(--appearance-panel-overlay-max-height);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
}

/* ── Header ── */

.appearance_floating_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 14px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.48);
}

.header_main {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.header_title {
  font-size: 18px;
  font-weight: 800;
  color: var(--surface-text-primary);
}

.header_actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.auto_saved_note {
  font-size: 12px;
  color: var(--brand-color-pure);
  background: rgba(var(--color-brand-1-rgb), 0.12);
  border-radius: var(--radius-pill);
  padding: 8px 12px;
  white-space: nowrap;
}

/* ── Preview 插槽（仅 overlay 模式下展示）── */

.appearance_floating_preview {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  padding: 18px 20px 0;
}

/* ── Body ── */

.appearance_floating_body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /*
   * 让默认插槽中的根面板（setting_software_theme / setting_keyboard_appearance）
   * 在 flex 容器中正确撑开高度并允许内部滚动；
   * 子组件根元素若设置了 height:100%，需要在 flex 父级显式声明 flex:1+min-height:0，
   * 否则会被父级反算缩到 0。
   */
  & > :deep(*) {
    flex: 1;
    min-height: 0;
    height: auto;
  }
}

/* ── 共用响应式（窄屏与矮屏的尺寸/间距收紧）── */

@media (max-height: 900px) {
  .appearance_floating_header {
    padding: 12px 20px 10px;
  }
}

@media (max-height: 750px) {
  .appearance_floating_header {
    padding: 10px 16px 8px;
    gap: 12px;
  }

  .header_title {
    font-size: 16px;
  }

  .auto_saved_note {
    padding: 6px 10px;
  }
}

@media (max-width: 960px) {
  .appearance_floating_anchor {
    top: 92px;
    left: 12px;
    right: 12px;

    &.centered_mode {
      top: auto;
      left: auto;
      right: auto;
      width: 100%;
    }
  }

  .appearance_floating_header {
    flex-direction: column;
    align-items: stretch;
  }

  .header_actions {
    flex-wrap: wrap;
    row-gap: 8px;
    justify-content: space-between;
  }

  .auto_saved_note {
    white-space: normal;
  }
}
</style>
